import React from "react";
import { motion } from "framer-motion";

// Global background layers — sits behind ALL content (z-index: 0).
// Adds:
//   • drifting gradient blobs
//   • faint dot grid pattern
//   • soft conic mesh
//   • grain texture
export default function BackgroundLayers() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
    >
      {/* Soft conic mesh */}
      <div
        className="absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(1200px 600px at 10% -10%, rgba(48,86,245,0.18), transparent 60%), radial-gradient(900px 500px at 110% 10%, rgba(15,44,90,0.16), transparent 55%), radial-gradient(800px 600px at 50% 110%, rgba(48,86,245,0.12), transparent 55%)",
        }}
      />

      {/* Faint dot grid */}
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "radial-gradient(rgba(15,44,90,0.18) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
          maskImage:
            "linear-gradient(to bottom, rgba(0,0,0,0.9), rgba(0,0,0,0.5) 35%, rgba(0,0,0,0.2) 70%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to bottom, rgba(0,0,0,0.9), rgba(0,0,0,0.5) 35%, rgba(0,0,0,0.2) 70%, transparent)",
        }}
      />

      {/* Floating blobs */}
      <motion.div
        animate={{ x: [0, 60, -20, 0], y: [0, -40, 20, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-[8%] -left-32 w-[420px] h-[420px] rounded-full bg-[#3056F5]/25 blur-3xl"
      />
      <motion.div
        animate={{ x: [0, -80, 30, 0], y: [0, 40, -30, 0] }}
        transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-[35%] -right-40 w-[520px] h-[520px] rounded-full bg-[#0F2C5A]/15 blur-3xl"
      />
      <motion.div
        animate={{ x: [0, 50, -50, 0], y: [0, -30, 40, 0] }}
        transition={{ duration: 32, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-[12%] left-[20%] w-[340px] h-[340px] rounded-full bg-[#3056F5]/15 blur-3xl"
      />

      {/* Grain */}
      <div
        className="absolute inset-0 opacity-[0.05] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
    </div>
  );
}

// Floating decorative shapes used inside specific sections for extra depth.
export function FloatingShapes({ variant = "default" }) {
  if (variant === "rupees") {
    return (
      <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
        {[
          { x: "8%", y: "20%", size: 22, d: 0, dur: 8 },
          { x: "85%", y: "30%", size: 32, d: 1.4, dur: 9 },
          { x: "16%", y: "70%", size: 18, d: 2.2, dur: 7 },
          { x: "78%", y: "78%", size: 26, d: 0.8, dur: 11 },
          { x: "50%", y: "12%", size: 20, d: 1.8, dur: 10 },
        ].map((s, i) => (
          <motion.div
            key={i}
            initial={{ y: 0, rotate: -8, opacity: 0.0 }}
            animate={{
              y: [0, -14, 0],
              rotate: [-8, 6, -8],
              opacity: [0.18, 0.32, 0.18],
            }}
            transition={{
              duration: s.dur,
              repeat: Infinity,
              ease: "easeInOut",
              delay: s.d,
            }}
            style={{
              left: s.x,
              top: s.y,
              fontSize: `${s.size}px`,
            }}
            className="absolute font-display text-[#0F2C5A]"
          >
            ₹
          </motion.div>
        ))}
      </div>
    );
  }

  // default — geometric rings & dots
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
        className="absolute top-[12%] right-[8%] w-24 h-24 border-2 border-dashed border-[#0F2C5A]/15 rounded-full"
      />
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 120, repeat: Infinity, ease: "linear" }}
        className="absolute bottom-[18%] left-[5%] w-32 h-32 border border-[#3056F5]/20 rounded-full"
      />
      <motion.div
        animate={{ y: [0, 14, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-[28%] left-[7%] w-3 h-3 bg-[#3056F5] rounded-full"
      />
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute bottom-[22%] right-[10%] w-2 h-2 bg-[#0F2C5A] rounded-full"
      />
    </div>
  );
}
